import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar, User, Phone, Mail, Search, Filter, RefreshCw, Star, CheckCircle, XCircle, UserPlus, CreditCard, AlertCircle, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getServiceName } from '../utils/serviceMapping';
import { mockServices } from '../mockData';
import AdminAssistedBooking from '../components/AdminAssistedBooking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Simple authentication (In production, use proper auth)
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === '8ka%ZJ0p!j%&1%Le$K2W') {  // Strong password
      setIsAuthenticated(true);
      toast.success('Logged in successfully');
    } else {
      toast.error('Invalid password');
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      // Use optimized API with stats
      const response = await axios.get(`${API}/bookings`, {
        params: {
          include_stats: true,
          limit: 100
        }
      });
      setBookings(response.data.bookings || response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/testimonials/pending`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 'bookings') {
        fetchBookings();
      } else if (activeTab === 'testimonials') {
        fetchTestimonials();
      }
    }
  }, [isAuthenticated, activeTab]);

  // Helper function to get formatted duration
  const getFormattedDuration = (booking) => {
    if (booking.consultation_duration === '5-10') {
      return 'Up to 10 mins';
    }

    // For "10+" duration, get the actual service duration
    if (booking.consultation_duration === '10+') {
      const service = mockServices.find(s => s.id === booking.service);
      if (service && service.duration) {
        return `Up to ${service.duration}`;
      }
      return '10+ mins';
    }

    return `${booking.consultation_duration} mins`;
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success('Status updated successfully');
      fetchBookings();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const approveTestimonial = async (testimonialId) => {
    try {
      await axios.patch(`${API}/testimonials/${testimonialId}/approve`);
      toast.success('Testimonial approved successfully');
      fetchTestimonials();
    } catch (error) {
      console.error('Error approving testimonial:', error);
      toast.error('Failed to approve testimonial');
    }
  };

  const deleteTestimonial = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) {
      return;
    }
    try {
      await axios.delete(`${API}/testimonials/${testimonialId}`);
      toast.success('Testimonial deleted successfully');
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentBadge = (status) => {
    const colors = {
      pending: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesFilter = filter === 'all' || booking.status === filter;
    const matchesSearch =
      booking.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.phone.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center pt-20">
        <Card className="p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold text-purple-900 mb-6 text-center">Admin Login</h2>
          <form onSubmit={handleLogin}>
            <Input
              type="password"
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mb-4"
            />
            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
              Login
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pt-20">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-purple-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage bookings and testimonials</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 flex-wrap">
          <Button
            onClick={() => setActiveTab('bookings')}
            variant={activeTab === 'bookings' ? 'default' : 'outline'}
            className={activeTab === 'bookings' ? 'bg-purple-600' : ''}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Bookings
          </Button>
          <Button
            onClick={() => setActiveTab('assisted-booking')}
            variant={activeTab === 'assisted-booking' ? 'default' : 'outline'}
            className={activeTab === 'assisted-booking' ? 'bg-purple-600' : ''}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Assisted Booking
          </Button>
          <Button
            onClick={() => setActiveTab('testimonials')}
            variant={activeTab === 'testimonials' ? 'default' : 'outline'}
            className={activeTab === 'testimonials' ? 'bg-purple-600' : ''}
          >
            <Star className="w-4 h-4 mr-2" />
            Testimonials ({testimonials.length})
          </Button>
        </div>

        <div style={{ display: activeTab === 'bookings' ? 'block' : 'none' }}>
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          {[
            { label: 'Total Bookings', value: bookings.length, color: 'purple', icon: Calendar },
            { label: 'Pending', value: bookings.filter(b => b.status === 'pending').length, color: 'yellow', icon: AlertCircle },
            { label: 'Confirmed', value: bookings.filter(b => b.status === 'confirmed').length, color: 'green', icon: CheckCircle },
            { label: 'Completed', value: bookings.filter(b => b.status === 'completed').length, color: 'blue', icon: Star },
            {
              label: 'Payment Pending',
              value: bookings.filter(b => b.payment_status === 'pending' && b.amount > 0).length,
              color: 'orange',
              icon: CreditCard
            }
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-gray-600 text-sm">{stat.label}</p>
                  {Icon && <Icon className={`w-5 h-5 text-${stat.color}-600`} />}
                </div>
                <p className="text-3xl font-bold text-purple-900">{stat.value}</p>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bookings</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={itemsPerPage.toString()} onValueChange={(value) => setItemsPerPage(Number(value))}>
              <SelectTrigger className="w-full md:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="25">25 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
                <SelectItem value="100">100 / page</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchBookings} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </Card>

        {/* Bookings List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading bookings...</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-600">No bookings found</p>
          </Card>
        ) : (
          <>
            {/* Results info */}
            <div className="mb-4 text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length} bookings
            </div>

            <div className="space-y-4">
              {paginatedBookings.map((booking) => (
                <Card key={booking.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Booking Info */}
                  <div className="flex-1">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-purple-900">{booking.name}</h3>
                          <Badge className={getStatusBadge(booking.status)}>
                            {booking.status}
                          </Badge>
                          {booking.amount === 0 ? (
                            <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Free
                            </Badge>
                          ) : (
                            <Badge className={getPaymentBadge(booking.payment_status) + ' flex items-center gap-1'}>
                              {booking.payment_status === 'completed' ? (
                                <>
                                  <CheckCircle className="w-3 h-3" />
                                  Payment Received ✓
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-3 h-3" />
                                  Payment Pending
                                </>
                              )}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            {booking.email}
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            {booking.phone}
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Astrologer: {booking.astrologer}
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {new Date(booking.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Service</p>
                        <p className="font-medium text-gray-900">{getServiceName(booking.service)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Duration</p>
                        <p className="font-medium text-gray-900">{getFormattedDuration(booking)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Amount</p>
                        <p className="font-medium text-green-600">
                          {booking.amount === 0 ? 'Free' : `₹${booking.amount / 100}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Payment Status</p>
                        <div className="flex items-center gap-1 mt-1">
                          {booking.amount === 0 ? (
                            <span className="font-medium text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              N/A (Free)
                            </span>
                          ) : booking.payment_status === 'completed' ? (
                            <span className="font-medium text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Received ✓
                            </span>
                          ) : (
                            <span className="font-medium text-orange-600 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 lg:w-48">
                    <Select
                      value={booking.status}
                      onValueChange={(value) => updateBookingStatus(booking.id, value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="text-xs">
                      View Details
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Card className="p-4 mt-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                  >
                    First
                  </Button>
                  <Button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>

                  {/* Page numbers */}
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          onClick={() => goToPage(pageNum)}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className={currentPage === pageNum ? "bg-purple-600" : ""}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                  >
                    Last
                  </Button>
                </div>
              </div>
            </Card>
          )}
          </>
        )}
          </>
        </div>

        <div style={{ display: activeTab === 'testimonials' ? 'block' : 'none' }}>
          <>
            {/* Testimonials Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-purple-900">
                Pending Testimonials ({testimonials.length})
              </h2>
              <Button onClick={fetchTestimonials} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {/* Testimonials List */}
            {loading ? (
              <Card className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading testimonials...</p>
              </Card>
            ) : testimonials.length === 0 ? (
              <Card className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
                <p className="text-gray-600">No pending testimonials to review.</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {testimonials.map((testimonial) => (
                  <Card key={testimonial.id} className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Testimonial Content */}
                      <div className="flex-1">
                        {/* Rating */}
                        <div className="flex gap-1 mb-3">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                          ))}
                        </div>

                        {/* Text */}
                        <p className="text-gray-700 text-lg mb-4 italic">
                          "{testimonial.text}"
                        </p>

                        {/* User Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-semibold">{testimonial.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">{testimonial.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">{testimonial.service}</span>
                          </div>
                          {testimonial.location && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">📍 {testimonial.location}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">
                              {new Date(testimonial.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2 lg:w-48">
                        <Button
                          onClick={() => approveTestimonial(testimonial.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => deleteTestimonial(testimonial.id)}
                          variant="destructive"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        </div>

        {/* Assisted Booking Tab - Always render but hide when not active */}
        <div style={{ display: activeTab === 'assisted-booking' ? 'block' : 'none' }}>
          <AdminAssistedBooking />
        </div>
      </div>
    </div>
  );
};

export default Admin;
